import type { Maybe } from '@adornis/base/utilTypes.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { context } from '@adornis/baseql/server/context.js';
import { ANY_CONTEXT } from '@adornis/users/db/a-roles.js';
import { AdornisUser } from '@adornis/users/db/a-user.js';
import type { UserPermission } from '@adornis/users/db/types.js';
// AllPermissions is defined in this file, no import needed
import { projectConfig } from '@chancenwerk/config/config.js';
import type { ChancenwerkUser } from './master-data-management/users/entities/ChancenwerkUser.js';
import { Role } from './semantics/Role.js';

// Disable eslint type fixing because it actually breaks the type and makes it a generic string
const makePermissions = <T extends readonly string[]>(permArray: T) =>
  permArray.reduce(
    (acc, curr) => ({ ...acc, [curr]: curr }),
    // eslint-disable-next-line @typescript-eslint/prefer-reduce-type-parameter, @typescript-eslint/consistent-type-assertions
    {} as {
      [key in (typeof permArray)[number]]: AllPermissions;
    },
  );

export enum ContextPrefix {
  SCHOOL = 'school_',
  STUDENT = 'student_',
  KPF = 'kpf_',
}

const globalPermissions = [
  'READ_NOTIFICATIONS',
  'READ_STUDENT',
  'READ_KPF',
  'READ_SCHOOL',
  'READ_EMPLOYEE',
  'READ_EMPLOYEE_PUBLIC',
  'EDIT_PERMISSIONS',
  'EDIT_NOTIFICATIONS',
  'READ_FINANCING',
  'READ_PRODUCT',
  'READ_TODOS',
  'CREATE_TODOS',
  'ASSIGN_TODOS_TO_OTHERS',
] as const;

const campusPermission = [
  'LOGIN_CAMPUS',
  'READ_EVENTS',
  'EDIT_EVENTS',
  'READ_CONTENT',
  'READ_HEFTE_CONTENT',
  'EDIT_CONTENT',
  'EDIT_CONTENT_EXTERNAL',
  'EDIT_GROUPS',
  'READ_STATISTICS',
  'READ_LOUNGE',
  'EDIT_PRESENCE',
  'READ_DIAGNOSTIC',
  'READ_DIAGNOSTIC_CUM',
  'ADMIN_EVENTS',
  'EXPORT_LEARNING_DATA',
  'EXPORT_LEARNING_DATA_MAPPING',
  'DELETE_LEVUMI_TESTS',
  'READ_LOUNGE_EXTERNAL',
  'CREATE_BLANKO',
  'EDIT_SCOPE',
  'VIEW_TEACHER_DASHBOARD',
  'VIEW_TEACHER_DASHBOARD_EMPLOYEE',
  'EDIT_LERNHEFTE',
  'EDIT_POLLS',
  'CHANCENKURVE_SETTINGS',
  'MANAGE_HEFTE_STUDENTS',
] as const;

const raumPermission = [
  'LOGIN_RAUM',

  'CREATE_KPF',
  'EDIT_KPF',
  'DELETE_KPF',

  'EDIT_STUDENT',
  'DELETE_STUDENT',
  'EDIT_STUDENT_SCHOOLINFORMATION',
  'EDIT_STUDENT_BASIC_INFO',

  'CREATE_SCHOOL',

  'READ_SCHOOL_DEFAULT',
  'READ_SCHOOL_SCHOOLINFORMATION',
  'READ_SCHOOL_MATERIAL',
  'READ_SCHOOL_ACQUISITION',
  'READ_SCHOOL_PRODUCT_ADDITIONAL_AGREEMENTS',
  'READ_SCHOOL_COOPERATION',
  'READ_SCHOOL_PRODUCTS',
  'READ_SCHOOL_SUPPORT',
  'READ_SCHOOL_MEDIA',
  'READ_SCHOOL_ROLES',
  'READ_SCHOOL_CAMPAIGN',

  'EDIT_SCHOOL',
  'EDIT_SCHOOL_DEFAULT',
  'EDIT_SCHOOL_SCHOOLINFORMATION',
  'EDIT_SCHOOL_MATERIAL',
  'EDIT_SCHOOL_ACQUISITION',
  'EDIT_SCHOOL_PRODUCT_ADDITIONAL_AGREEMENTS',
  'EDIT_SCHOOL_COOPERATION',
  'EDIT_SCHOOL_PRODUCTS',
  'EDIT_SCHOOL_SUPPORT',
  'EDIT_SCHOOL_MEDIA',
  'EDIT_SCHOOL_ROLES',
  'EDIT_SCHOOL_CAMPAIGN',

  'CREATE_PRODUCT_IDEAS',
  'READ_PRODUCT_IDEAS',
  'EDIT_PRODUCT_IDEAS',
  'DELETE_PRODUCT_IDEAS',

  'DELETE_SCHOOL',
  'EDIT_EMPLOYEE',
  'DELETE_EMPLOYEE',
  'DELETE_OS_ACQUISITION',

  'EDIT_EXTERNAL_CONTACT_PERSON',
  'DELETE_EXTERNAL_CONTACT_PERSON',
  'EDIT_EXTERNAL_CONTACT_PERSON_CONTACT_INFORMATION',
  'READ_EXTERNAL_CONTACT_PERSON_CONTACT_INFORMATION',

  'READ_FINANCING',
  'EDIT_FINANCING',
  'EDIT_PRODUCT',
  'EDIT_WORKSHOP',
  'EXPORT_STUDENT',
  'EXPORT_EMPLOYEE',
  'EXPORT_CONTRACT',
  'EXPORT_CUSTOM_LIST',
  'EXPORT_ATTENDANCE_OVERVIEW',
  'CREATE_EMPLOYEE',
  'CREATE_NOTE',
  'VIEW_NOTES',
  'VIEW_ACQUISITION',
  'VIEW_DUPLICATES',
  'MIGRATE_BUT_YEAR',
  'CHANGE_STUDENT_PASSWORD',
  'CHANGE_PASSWORD',
  'EXPORT_KPF_FUNDING',
  'VIEW_CRM',
  'EDIT_EVALUATION',
] as const;
const logistikPermissions = ['LOGIN_LOGISTIK', 'DELETE_LOGISTIK_ORDER'] as const;
const heftePermissions = [] as const;

const boardPermissions = [
  'LOGIN_BOARD',
  'VIEW_ATTENDANCE',
  'VIEW_ATTENDANCE_DIGITAL',
  'EXPORT_ATTENDANCE',
  'EXPORT_ATTENDANCE_HISTORY',
  'VIEW_KPF_OVERVIEW',
  'VIEW_DIGITAL_COURSES',
  'VIEW_WEEK_OVERVIEW',
  'MANAGE_KPF_BOARD',
  'EDIT_COURSE',
  'EDIT_TEAM_TREFFEN',
  'EDIT_WORK_TRACKING',
  'EDIT_WORK_TRACKING_EVERYTHING',
] as const;

export const netzPermissions = [
  'LOGIN_NETZ',
  'CREATE_WIKI_ENTRY',
  'EDIT_WIKI_ENTRY',
  'DELETE_WIKI_ENTRY',
  'WIKI_QM',
  'WIKI_EMPLOYEES',
  'WIKI_VIEW_DRAFT_OVERVIEW',
  'WIKI_PUBLISH_DRAFT',
  'WIKI_MODIFY_SETTINGS',
  'WIKI_PUBLISH_PAGE',
  'WIKI_VIEW_FILES',
] as const;

const kraeftePermissions = ['LOGIN_KRAEFTE'] as const;

const akademiePermissions = ['LOGIN_AKADEMIE', 'EDIT_WORKSHOP'] as const;

const marktPermissions = ['LOGIN_MARKT'] as const;

const familyPermissions = ['LOGIN_FAMILY'] as const;

export type AllPermissions =
  | (typeof globalPermissions)[number]
  | (typeof raumPermission)[number]
  | (typeof campusPermission)[number]
  | (typeof logistikPermissions)[number]
  | (typeof boardPermissions)[number]
  | (typeof netzPermissions)[number]
  | (typeof kraeftePermissions)[number]
  | (typeof akademiePermissions)[number]
  | (typeof marktPermissions)[number]
  | (typeof familyPermissions)[number]
  | UserPermission;

export type Service =
  | 'RAUM_URL'
  | 'CAMPUS_URL'
  | 'NETZ_URL'
  | 'LOGISTIK_URL'
  | 'BOARD_URL'
  | 'KRAEFTE_URL'
  | 'HEFTE_URL'
  | 'AKADEMIE_URL'
  | 'MARKT_URL'
  | 'FAMILY_URL';

export const ChancenPermission = {
  global: makePermissions(globalPermissions),
  raum: makePermissions(raumPermission),
  campus: makePermissions(campusPermission),
  logistik: makePermissions(logistikPermissions),
  board: makePermissions(boardPermissions),
  netz: makePermissions(netzPermissions),
  kraefte: makePermissions(kraeftePermissions),
  hefte: makePermissions(heftePermissions),
  akademie: makePermissions(akademiePermissions),
  markt: makePermissions(marktPermissions),
  family: makePermissions(familyPermissions),
};

type PermissionNameMap = Record<AllPermissions, string>;

export function constructPermissionTag(name: string, categoryTags?: string[][], inlineTags?: [string?, string?]) {
  let tags: string[] = [];

  // Build categoryTags with index
  if (categoryTags) {
    for (let i = 0; i < categoryTags?.length; i++) {
      const categoryTag = categoryTags.at(i);
      if (!categoryTag) continue;

      tags = tags.concat(categoryTag.map(tag => `${i + 1}${tag}`));
    }
  }

  if (inlineTags) {
    tags = tags.concat(inlineTags.map(tag => `#${tag}`));
  }

  return `${tags.join('_')}|${name}`;
}

// @ts-expect-error Missing properties are legacy
const permissionTags: PermissionNameMap = {
  EDIT_PERMISSIONS: constructPermissionTag('Berechtigungen bearbeiten'),

  // ? add crud
  READ_STUDENT: constructPermissionTag('Schüler:innen lesen', [['CHANCENraum'], ['Schüler:innen']]),
  EDIT_STUDENT: constructPermissionTag('Schüler:innen bearbeiten', [['CHANCENraum'], ['Schüler:innen']]),
  EDIT_STUDENT_SCHOOLINFORMATION: constructPermissionTag('Schüler:innen Jahrgangsstufen bestätigen', [
    ['CHANCENraum'],
    ['Schüler:innen'],
  ]),
  EDIT_STUDENT_BASIC_INFO: constructPermissionTag('Schüler:innen Basisinformationen bearbeiten', [
    ['CHANCENraum'],
    ['Schüler:innen'],
  ]),
  DELETE_STUDENT: constructPermissionTag('Schüler:innen löschen', [['CHANCENraum'], ['Schüler:innen']]),

  // ? add crud
  CREATE_KPF: constructPermissionTag('KPF anlegen', [['CHANCENraum'], ['KPF']]),
  READ_KPF: constructPermissionTag('KPF lesen', [['CHANCENraum'], ['KPF']]),
  EDIT_KPF: constructPermissionTag('KPF bearbeiten', [['CHANCENraum'], ['KPF']]),
  DELETE_KPF: constructPermissionTag('KPF löschen', [['CHANCENraum'], ['KPF']]),

  CREATE_SCHOOL: constructPermissionTag('Kooperationspartner anlegen', [['CHANCENraum'], ['Kooperationspartner']]),

  READ_SCHOOL: constructPermissionTag(
    'Kooperationspartner lesen',
    [['CHANCENraum'], ['Kooperationspartner']],
    ['read'],
  ),
  READ_SCHOOL_DEFAULT: constructPermissionTag(
    'Kooperationspartner allgemeine Informationen lesen',
    [['CHANCENraum'], ['Kooperationspartner']],
    ['read', 'default'],
  ),
  READ_SCHOOL_SCHOOLINFORMATION: constructPermissionTag(
    'Kooperationspartner Schulinformationen lesen',
    [['CHANCENraum'], ['Kooperationspartner']],
    ['read', 'schoolinformation'],
  ),
  READ_SCHOOL_MATERIAL: constructPermissionTag(
    'Kooperationspartner Material lesen',
    [['CHANCENraum'], ['Kooperationspartner']],
    ['read', 'material'],
  ),
  READ_SCHOOL_MEDIA: constructPermissionTag(
    'Medien lesen',
    [['CHANCENraum'], ['Kooperationspartner']],
    ['read', 'material'],
  ),
  READ_SCHOOL_ACQUISITION: constructPermissionTag(
    'Kooperationspartner Akquise lesen',
    [['CHANCENraum'], ['Kooperationspartner']],
    ['read', 'acquisition'],
  ),
  READ_SCHOOL_PRODUCT_ADDITIONAL_AGREEMENTS: constructPermissionTag(
    'Kooperationspartner Zusatzvereinbarung lesen',
    [['CHANCENraum'], ['Kooperationspartner']],
    ['read', 'product additional agreements'],
  ),
  READ_SCHOOL_COOPERATION: constructPermissionTag(
    'KooperationspartnerZusammenarbeit lesen',
    [['CHANCENraum'], ['Kooperationspartner']],
    ['read', 'cooperation'],
  ),
  READ_SCHOOL_PRODUCTS: constructPermissionTag(
    'Kooperationspartner Produkte lesen',
    [['CHANCENraum'], ['Kooperationspartner']],
    ['read', 'products'],
  ),
  READ_SCHOOL_SUPPORT: constructPermissionTag(
    'Kooperationspartner Ausgehende Förderungen lesen',
    [['CHANCENraum'], ['Kooperationspartner']],
    ['read', 'support'],
  ),
  READ_SCHOOL_ROLES: constructPermissionTag(
    'Kooperationspartner übergeordnete Rollen lesen',
    [['CHANCENraum'], ['Kooperationspartner']],
    ['read', 'roles'],
  ),
  READ_SCHOOL_CAMPAIGN: constructPermissionTag(
    'Kooperationspartner Kampagnenselektion lesen',
    [['CHANCENraum'], ['Kooperationspartner']],
    ['read', 'campaign'],
  ),
  EDIT_SCHOOL: constructPermissionTag(
    'Kooperationspartner bearbeiten',
    [['CHANCENraum'], ['Kooperationspartner']],
    ['edit'],
  ),
  EDIT_SCHOOL_DEFAULT: constructPermissionTag(
    'Kooperationspartner allgemeine Informationen bearbeiten',
    [['CHANCENraum'], ['Kooperationspartner']],
    ['edit', 'default'],
  ),
  EDIT_SCHOOL_MEDIA: constructPermissionTag(
    'Kooperationspartner Medien bearbeiten',
    [['CHANCENraum'], ['Kooperationspartner']],
    ['edit', 'material'],
  ),
  EDIT_SCHOOL_SCHOOLINFORMATION: constructPermissionTag(
    'Kooperationspartner Schulinformationen bearbeiten',
    [['CHANCENraum'], ['Kooperationspartner']],
    ['edit', 'schoolinformation'],
  ),
  EDIT_SCHOOL_MATERIAL: constructPermissionTag(
    'Kooperationspartner Material bearbeiten',
    [['CHANCENraum'], ['Kooperationspartner']],
    ['edit', 'material'],
  ),
  EDIT_SCHOOL_ACQUISITION: constructPermissionTag(
    'Kooperationspartner Akquise bearbeiten',
    [['CHANCENraum'], ['Kooperationspartner']],
    ['edit', 'acquisition'],
  ),
  EDIT_SCHOOL_PRODUCT_ADDITIONAL_AGREEMENTS: constructPermissionTag(
    'Kooperationspartner Zusatzvereinbarung bearbeiten',
    [['CHANCENraum'], ['Kooperationspartner']],
    ['edit', 'product additional agreements'],
  ),
  EDIT_SCHOOL_COOPERATION: constructPermissionTag(
    'Kooperationspartner Zusammenarbeit bearbeiten',
    [['CHANCENraum'], ['Kooperationspartner']],
    ['edit', 'cooperation'],
  ),
  EDIT_SCHOOL_PRODUCTS: constructPermissionTag(
    'Kooperationspartner Produkte bearbeiten',
    [['CHANCENraum'], ['Kooperationspartner']],
    ['edit', 'products'],
  ),
  EDIT_SCHOOL_SUPPORT: constructPermissionTag(
    'Kooperationspartner Ausgehende Förderungen bearbeiten',
    [['CHANCENraum'], ['Kooperationspartner']],
    ['edit', 'support'],
  ),
  EDIT_SCHOOL_ROLES: constructPermissionTag(
    'Kooperationspartner übergeordnete Rollen bearbeiten',
    [['CHANCENraum'], ['Kooperationspartner']],
    ['edit', 'roles'],
  ),
  EDIT_SCHOOL_CAMPAIGN: constructPermissionTag(
    'Kooperationspartner Kampagnenselektion bearbeiten',
    [['CHANCENraum'], ['Kooperationspartner']],
    ['edit', 'campaign'],
  ),
  DELETE_SCHOOL: constructPermissionTag('Kooperationspartner löschen', [['CHANCENraum'], ['Kooperationspartner']]),

  CREATE_PRODUCT_IDEAS: constructPermissionTag('Produktidee anlegen', [['CHANCENraum'], ['Produktideen']]),
  READ_PRODUCT_IDEAS: constructPermissionTag('Produktidee lesen', [['CHANCENraum'], ['Produktideen']]),
  EDIT_PRODUCT_IDEAS: constructPermissionTag('Produktidee bearbeiten', [['CHANCENraum'], ['Produktideen']]),
  DELETE_PRODUCT_IDEAS: constructPermissionTag('Produktidee löschen', [['CHANCENraum'], ['Produktideen']]),

  CREATE_EMPLOYEE: constructPermissionTag('Mitarbeitende anlegen', [['CHANCENraum'], ['Mitarbeitende']]),
  READ_EMPLOYEE: constructPermissionTag('Mitarbeitende lesen', [['CHANCENraum'], ['Mitarbeitende']], ['read']),
  READ_EMPLOYEE_PUBLIC: constructPermissionTag(
    'Mitarbeitende öffentliche Daten lesen',
    [['CHANCENraum'], ['Mitarbeitende']],
    ['read', 'public'],
  ),
  EDIT_EMPLOYEE: constructPermissionTag('Mitarbeitende bearbeiten', [['CHANCENraum'], ['Mitarbeitende']]),
  DELETE_EMPLOYEE: constructPermissionTag('Mitarbeitende löschen', [['CHANCENraum'], ['Mitarbeitende']]),
  DELETE_OS_ACQUISITION: constructPermissionTag('OS-Interessenten löschen', [['CHANCENraum']]),

  // ? add crud
  READ_NOTIFICATIONS: constructPermissionTag('Neuigkeiten lesen', [['CHANCENcampus'], ['Neuigkeiten']]),
  EDIT_NOTIFICATIONS: constructPermissionTag('Neuigkeiten bearbeiten', [['CHANCENcampus'], ['Neuigkeiten']]),

  // CREATE_FINANCING: constructPermissionTag('Finanzierungsformen anlegen', [['CHANCENraum'], ['CMS'], ['Finanzierungsformen']]),
  READ_FINANCING: constructPermissionTag('Finanzierungsformen lesen', [['CHANCENraum'], ['Finanzierungsformen']]),
  EDIT_FINANCING: constructPermissionTag('Finanzierungsformen bearbeiten', [['CHANCENraum'], ['Finanzierungsformen']]),
  // DELETE_FINANCING: constructPermissionTag('Finanzierungsformen löschen', [['CHANCENraum'], ['CMS'], ['Finanzierungsformen']]),

  // CREATE_PRODUCT: constructPermissionTag('Produktarten anlegen', [['CHANCENraum'], ['CMS'], ['Produktarten']]),
  READ_PRODUCT: constructPermissionTag('Produkte lesen', [['CHANCENraum'], ['Produkte']]),
  EDIT_PRODUCT: constructPermissionTag('Produkte bearbeiten', [['CHANCENraum'], ['Produkte']]),
  // DELETE_PRODUCT: constructPermissionTag('Produktarten löschen', [['CHANCENraum'], ['CMS'], ['Produktarten']]),

  //** Login permissions */
  LOGIN_CAMPUS: constructPermissionTag('CHANCENcampus Zugriff', [['CHANCENcampus', 'Applikationszugriff']]),
  LOGIN_RAUM: constructPermissionTag('CHANCENraum Zugriff', [['CHANCENraum', 'Applikationszugriff']]),
  LOGIN_NETZ: constructPermissionTag('CHANCENnetz Zugriff', [['CHANCENnetz', 'Applikationszugriff']]),
  LOGIN_BOARD: constructPermissionTag('CHANCENboard Zugriff', [['CHANCENboard', 'Applikationszugriff']]),
  LOGIN_LOGISTIK: constructPermissionTag('CHANCENlogistik Zugriff', [['CHANCENlogistik', 'Applikationszugriff']]),
  LOGIN_KRAEFTE: constructPermissionTag('CHANCENkräfte Zugriff', [['CHANCENkräfte', 'Applikationszugriff']]),
  LOGIN_AKADEMIE: constructPermissionTag('CHANCENakademie Zugriff', [['CHANCENakademie', 'Applikationszugriff']]),
  LOGIN_MARKT: constructPermissionTag('CHANCENcredits Zugriff', [['CHANCENcredits', 'Applikationszugriff']]),
  LOGIN_FAMILY: constructPermissionTag('CHANCENfamily Zugriff', [['CHANCENfamily', 'Applikationszugriff']]),

  //** Campus Events to show different events, times and other things to students */
  READ_EVENTS: constructPermissionTag('Events lesen', [['CHANCENcampus'], ['Events']]),
  EDIT_EVENTS: constructPermissionTag('Events bearbeiten', [['CHANCENcampus'], ['Events']]),
  ADMIN_EVENTS: constructPermissionTag('Events Verwaltung lesen', [['CHANCENcampus'], ['Events']]),

  READ_DIAGNOSTIC: constructPermissionTag('CHANCENkurve lesen', [['CHANCENcampus'], ['Auswertung']]),
  READ_DIAGNOSTIC_CUM: constructPermissionTag('Kumulative Auswertung lesen', [['CHANCENcampus'], ['Auswertung']]),
  DELETE_LEVUMI_TESTS: constructPermissionTag('CHANCENkurve Testergebnis löschen', [['CHANCENcampus'], ['Auswertung']]),

  READ_CONTENT: constructPermissionTag('Lerninhalte lesen', [['CHANCENcampus'], ['Lerninhalte']]),
  READ_HEFTE_CONTENT: constructPermissionTag('CHANCENhefte Lerninhalte lesen', [['CHANCENcampus'], ['CHANCENhefte']]),
  EDIT_CONTENT: constructPermissionTag('Lerninhalte bearbeiten', [['CHANCENcampus'], ['Lerninhalte']]),
  EDIT_CONTENT_EXTERNAL: constructPermissionTag('CHANCENcorner externe Inhalte bearbeiten', [
    ['CHANCENcampus'],
    ['Lerninhalte'],
  ]),
  READ_LOUNGE: constructPermissionTag('CHANCENcorner interne Inhalte lesen', [['CHANCENcampus'], ['Lerninhalte']]),
  READ_LOUNGE_EXTERNAL: constructPermissionTag('CHANCENcorner externe Inhalte lesen', [
    ['CHANCENcampus'],
    ['Lerninhalte'],
  ]),

  EDIT_GROUPS: constructPermissionTag('Gruppen bearbeiten', [['CHANCENcampus']]),
  READ_STATISTICS: constructPermissionTag('Zugangsstatistiken lesen', [['CHANCENcampus']]),
  VIEW_TEACHER_DASHBOARD: constructPermissionTag('Lehrer:innen Dashboard lesen', [
    ['CHANCENcampus'],
    ['Lehrer:innen Dashboard'],
  ]),
  VIEW_TEACHER_DASHBOARD_EMPLOYEE: constructPermissionTag('Mitarbeiter:innen Dashboard lesen', [
    ['CHANCENcampus'],
    ['Lehrer:innen Dashboard'],
  ]),
  EDIT_LERNHEFTE: constructPermissionTag('Lernhefte bearbeiten', [['CHANCENcampus']]),
  EDIT_POLLS: constructPermissionTag('Wirkungsmessung bearbeiten', [['CHANCENcampus']]),
  CHANCENKURVE_SETTINGS: constructPermissionTag('CHANCENkurve-Einstellungen bearbeiten', [
    ['CHANCENcampus'],
    ['CHANCENkurve'],
  ]),
  MANAGE_HEFTE_STUDENTS: constructPermissionTag('CHANCENhefte Schüler:innen verwalten', [
    ['CHANCENcampus'],
    ['CHANCENhefte'],
  ]),
  EXPORT_STUDENT: constructPermissionTag('Schüler:innen exportieren', [['CHANCENraum', 'Exportrechte']]),
  EXPORT_EMPLOYEE: constructPermissionTag('Mitarbeitende exportieren', [['CHANCENraum', 'Exportrechte']]),
  EXPORT_CONTRACT: constructPermissionTag('Vertrag exportieren', [['CHANCENraum', 'Exportrechte']]),
  EXPORT_CUSTOM_LIST: constructPermissionTag('individuelle Listenansichten exportieren', [
    ['CHANCENraum', 'Exportrechte'],
  ]),
  EXPORT_ATTENDANCE_OVERVIEW: constructPermissionTag('Anwesenheit monatlich exportieren', [
    ['CHANCENraum', 'Exportrechte'],
  ]),
  EXPORT_KPF_FUNDING: constructPermissionTag('KPF-Förderung exportieren', [['CHANCENraum', 'Exportrechte']]),
  EXPORT_ATTENDANCE: constructPermissionTag('Anwesenheit exportieren', [['CHANCENboard', 'Exportrechte']]),
  EXPORT_ATTENDANCE_HISTORY: constructPermissionTag('Anwesenheitexport-Historie exportieren', [
    ['CHANCENraum', 'Exportrechte'],
  ]),
  EXPORT_LEARNING_DATA: constructPermissionTag('Lerndaten exportieren', [['CHANCENcampus', 'Exportrechte']]),
  EXPORT_LEARNING_DATA_MAPPING: constructPermissionTag('Lerndaten-Schüler:innenschlüssel exportieren', [
    ['CHANCENcampus', 'Exportrechte'],
  ]),

  VIEW_ATTENDANCE: constructPermissionTag('Anwesenheit lesen', [['CHANCENboard'], ['Anwesenheit']]),
  VIEW_ATTENDANCE_DIGITAL: constructPermissionTag('Anwesenheit lesen digitale Kurse', [
    ['CHANCENboard'],
    ['Anwesenheit'],
  ]),
  EDIT_PRESENCE: constructPermissionTag('Anwesenheit bearbeiten', [['CHANCENboard'], ['Anwesenheit']]),
  MANAGE_KPF_BOARD: constructPermissionTag('Vertretung lesen', [['CHANCENboard'], ['Anwesenheit']]),
  EDIT_COURSE: constructPermissionTag('Kurse bearbeiten', [['CHANCENboard']]),
  EDIT_TEAM_TREFFEN: constructPermissionTag('Team Treffen bearbeiten', [['CHANCENboard']]),

  EDIT_WORKSHOP: constructPermissionTag('Workshops bearbeiten', [['CHANCENraum'], ['Workshops']]),

  CREATE_NOTE: constructPermissionTag('Dokumentationen anlegen', [['CHANCENraum'], ['Dokumentationen']]),
  VIEW_NOTES: constructPermissionTag('Dokumentationen lesen', [['CHANCENraum'], ['Dokumentationen']]),

  VIEW_ACQUISITION: constructPermissionTag('OS-Akquise lesen', [['CHANCENraum']]),
  VIEW_DUPLICATES: constructPermissionTag('Duplikate lesen', [['CHANCENraum']]),
  MIGRATE_BUT_YEAR: constructPermissionTag('BuT-Jahr aktualisieren', [['CHANCENraum']]),
  CHANGE_STUDENT_PASSWORD: constructPermissionTag('Schüler:innen Passwörter ändern', [
    ['CHANCENraum'],
    ['Schüler:innen'],
  ]),
  CHANGE_PASSWORD: constructPermissionTag('Alle Passwörter ändern', [['CHANCENraum']]),

  VIEW_KPF_OVERVIEW: constructPermissionTag('KPF-Übersicht lesen', [['CHANCENboard']]),
  VIEW_DIGITAL_COURSES: constructPermissionTag('Digitale Kurse lesen', [['CHANCENboard']]),
  VIEW_WEEK_OVERVIEW: constructPermissionTag('Wochenübersicht lesen', [['CHANCENboard']]),
  EDIT_WORK_TRACKING: constructPermissionTag('Tätigkeiten bearbeiten', [['CHANCENboard']]),
  EDIT_WORK_TRACKING_EVERYTHING: constructPermissionTag('Tätigkeiten vollen Zugriff', [['CHANCENboard']]),

  DELETE_LOGISTIK_ORDER: constructPermissionTag('Bestellung löschen', [['CHANCENlogistik'], ['Bestellungen']]),

  // chancennetz
  CREATE_WIKI_ENTRY: constructPermissionTag('CHANCENwiki Einträge erstellen', [['CHANCENwiki']]),
  EDIT_WIKI_ENTRY: constructPermissionTag('CHANCENwiki Einträge bearbeiten', [['CHANCENwiki']]),
  DELETE_WIKI_ENTRY: constructPermissionTag('CHANCENwiki Einträge löschen', [['CHANCENwiki']]),
  WIKI_QM: constructPermissionTag('CHANCENwiki QM', [['CHANCENwiki']]),
  WIKI_EMPLOYEES: constructPermissionTag('CHANCENwiki feste Mitarbeitende', [['CHANCENwiki']]),
  WIKI_VIEW_FILES: constructPermissionTag('CHANCENwiki Ansichtsrecht der Dateiverwaltung', [['CHANCENwiki']]),
  WIKI_VIEW_DRAFT_OVERVIEW: constructPermissionTag('CHANCENwiki Ansichtsrecht der Entwurfsübersicht', [
    ['CHANCENwiki'],
  ]),
  WIKI_PUBLISH_DRAFT: constructPermissionTag('Wiki veröffentlichung eines Entwurfs', [['CHANCENwiki']]),
  WIKI_PUBLISH_PAGE: constructPermissionTag('Wiki veröffentlichung einer Seite', [['CHANCENwiki']]),
  WIKI_MODIFY_SETTINGS: constructPermissionTag('Wiki Bearbeiten der Einstellungen', [['CHANCENwiki']]),

  EDIT_EXTERNAL_CONTACT_PERSON: constructPermissionTag('Externe Kontaktperson bearbeiten', [
    ['CHANCENraum'],
    ['Externe Kontaktperson'],
  ]),
  DELETE_EXTERNAL_CONTACT_PERSON: constructPermissionTag('Externe Kontaktperson löschen', [
    ['CHANCENraum'],
    ['Externe Kontaktperson'],
  ]),
  EDIT_EXTERNAL_CONTACT_PERSON_CONTACT_INFORMATION: constructPermissionTag(
    'Externe Kontaktperson private Kontaktdaten bearbeiten',
    [['CHANCENraum'], ['Externe Kontaktperson']],
  ),
  READ_EXTERNAL_CONTACT_PERSON_CONTACT_INFORMATION: constructPermissionTag(
    'Externe Kontaktperson private Kontaktdaten einsehen',
    [['CHANCENraum'], ['Externe Kontaktperson']],
  ),

  CREATE_TODOS: constructPermissionTag('ToDos anlegen', [['CHANCENraum'], ['ToDos']]),
  READ_TODOS: constructPermissionTag('ToDos lesen', [['CHANCENraum'], ['ToDos']]),
  ASSIGN_TODOS_TO_OTHERS: constructPermissionTag('ToDos anderen Personen zuweisen', [['CHANCENraum'], ['ToDos']]),

  EDIT_EVALUATION: constructPermissionTag('Evaluation lesen & bearbeiten', [['CHANCENraum']]),

  // Legacy
  CREATE_BLANKO: constructPermissionTag('#Archiv# Campus Blanko-Accounts anlegen', [['Archiv']]),
  EDIT_SCOPE: constructPermissionTag('#Archiv# Campus Mandantenfähige Scopes lesen', [['Archiv']]),
  VIEW_CRM: constructPermissionTag('#Archiv# CMS/Kampagnenselektion', [['Archiv']]),
};

export function getPermissionName(permission: AllPermissions): string {
  const tagWithName = permissionTags[permission];
  if (!tagWithName) throw new Error('No entry in the tags map found for permission: ' + permission);

  const name = tagWithName.split('|')[1];
  if (name === undefined)
    throw new Error('Tag found but has no | are you using the construct function for tags?' + permission);

  return name.trim();
}

export function getTagsOfPermissions(permission: AllPermissions): string[] {
  const tagWithName = permissionTags[permission];
  if (!tagWithName) throw new Error('No entry in the tags map found for permission: ' + permission);

  const tags = tagWithName.split('|')[0];
  if (tags === undefined)
    throw new Error('Tag found but has no | are you using the construct function for tags? ' + permission);

  return tags.split('_');
}

export function getTagsWithSpecificDepth(tags: string[], depth: number) {
  return tags.filter(tag => {
    const depthString = depth.toString();
    return tag.slice(0, depthString.length) === depthString;
  });
}

/**
 * User may only have roles in a specific KPF (or multiple roles in different KPFs).
 * For example the User is schuko on KPF X and project-manager on KPF Y.
 * Therefore they may only view the employees or edit the students of one or multiple specific KPFs, they are assigned to.
 * For example this function provides you with all KPFs the user has a specific role for
 * @param role The Role to check in the contexts of the KPFs
 * @param user The User to check
 * @returns A Promise with a String-Array containing the id's of the KPFs the user has a Role on.
 */
export function getAccessibleKPFIdsForUsersRole(role: Role | string, user: Maybe<ChancenwerkUser>): string[] {
  if (!user) return [];
  const contexts = user.roles.find(r => r.name === role)?.contexts;
  if (!contexts) return [];
  const context = contexts.find(c => c.startsWith(ContextPrefix.KPF));
  if (!context) return [];
  return [context.replace(new RegExp(`^${ContextPrefix.KPF}`), '')];
}

/**
 * The user might go to a service in which he doesnt have permission. In this case we probably want to show him a way back. This function uses the permissions of the user to determine the most likely place he wants to go to. Could be made more complex in the future.
 * @param userID The User to generate a rereoute link for
 * @returns An absolute reroute link for the user to the next best service he has permission on. If the user does not exist it reroutes to the campus
 */
export async function getNextServiceWithPermission(userID: string) {
  const user = await getByID(AdornisUser, userID)({ _id: 1, _class: 1, roles: { _id: 1, _class: 1, name: 1 } });
  if (!user) return projectConfig.get('CAMPUS_URL') + 'chancencampus/student/dashboard';

  // User could have multiple roles. We save them and prioritize them in this order at the end.
  let isStudent = false;
  let isExternalTeacherOrContact = false;
  let isAdmin = false;
  let isEditor = false;
  let isLeKo = false;
  let isLogistik = false;
  let isNotYetOnboardedMitarbeiter = false;

  // * Role checking
  for (const role of user.roles) {
    if (role.name === Role.STUDENT) isStudent = true;
    if (role.name === Role.HEFTE_STUDENT) isStudent = true; //currently we use the same page for students and hefteStudents, so we treat them the same
    if (role.name === Role.EXTERNAL_CONTACT || role.name === Role.EXTERNAL_TEACHER) isExternalTeacherOrContact = true;
    if (role.name === Role.ADMIN) isAdmin = true;
    if (role.name === Role.EDITOR) isEditor = true;
    if (role.name === Role.LEKO) isLeKo = true;
    if (role.name === Role.LOGISTICS_STAFF) isLogistik = true;
  }

  // * Onboarding check for Mitarbeiter
  // A Mitarbeiter (or subclass) who hasn't completed contract onboarding yet should be funneled
  // back into the onboarding flow rather than a generic fallback area.
  // NOTE: Mitarbeiter is dynamically imported to break a circular init-time dependency:
  // permissions.ts -> Mitarbeiter -> KPF -> AcquisitionRecord -> Student -> ChancenwerkUser,
  // where Student's `extends ChancenwerkUser` would run before ChancenwerkUser finished loading.
  const MA_CLASSES = ['Mitarbeiter', 'ExternerMitarbeiter'];
  if (user._class && MA_CLASSES.includes(user._class)) {
    //! dynamically imported to break circular init-time dependency
    const { Mitarbeiter } = await import('./master-data-management/users/entities/Mitarbeiter.js');
    const ma = await getByID(Mitarbeiter, userID)({ _id: 1, onboarding: { contractDocumentsConfirmed: 1 } });
    isNotYetOnboardedMitarbeiter = ma?.onboarding?.contractDocumentsConfirmed !== true;
  }

  // * Redirect logic
  if (isStudent) return projectConfig.get('CAMPUS_URL') + 'chancencampus/student/dashboard';
  if (isExternalTeacherOrContact) return projectConfig.get('CAMPUS_URL') + 'chancencampus/student/dashboard';
  if (isAdmin) return projectConfig.get('RAUM_URL');
  if (isEditor) return projectConfig.get('CAMPUS_URL') + 'chancencampus/student/dashboard';
  if (isLeKo) return projectConfig.get('CAMPUS_URL') + 'chancencampus/student/dashboard';
  if (isLogistik) return projectConfig.get('LOGISTIK_URL');
  if (isNotYetOnboardedMitarbeiter) return projectConfig.get('NETZ_URL') + 'onboarding';

  // * Default return to raum for all administrative roles
  return projectConfig.get('NETZ_URL');
}

/**
 * This function returns an array of service name in which the user has the login permission
 * @param userID The User to generate the array for
 * @returns An array of service config URL keys for which the user has the login permission
 */
export async function getAllServicesWithPermission(userID: string): Promise<Service[]> {
  const user = await AdornisUser.getByID<AdornisUser>(userID)({
    _id: 1,
    _class: 1,
    roles: { _id: 1, _class: 1, name: 1 },
  });
  if (!user) return [];

  const loginPermissionsForServices: string[] = [];

  for (const role of user.roles) {
    if (role.permissions.includes(ChancenPermission.campus.LOGIN_CAMPUS))
      loginPermissionsForServices.push('CAMPUS_URL');
    if (role.permissions.includes(ChancenPermission.raum.LOGIN_RAUM)) loginPermissionsForServices.push('RAUM_URL');
    // can add this without any conditions because logistik is publicly available
    loginPermissionsForServices.push('LOGISTIK_URL');
    if (role.permissions.includes(ChancenPermission.board.LOGIN_BOARD)) loginPermissionsForServices.push('BOARD_URL');
    if (role.permissions.includes(ChancenPermission.netz.LOGIN_NETZ)) loginPermissionsForServices.push('NETZ_URL');
    if (role.permissions.includes(ChancenPermission.kraefte.LOGIN_KRAEFTE))
      loginPermissionsForServices.push('KRAEFTE_URL');
    if (role.permissions.includes(ChancenPermission.akademie.LOGIN_AKADEMIE))
      loginPermissionsForServices.push('AKADEMIE_URL');
    if (role.permissions.includes(ChancenPermission.markt.LOGIN_MARKT)) loginPermissionsForServices.push('MARKT_URL');
    if (role.permissions.includes(ChancenPermission.family.LOGIN_FAMILY))
      loginPermissionsForServices.push('FAMILY_URL');
  }

  // @ts-expect-error Mapping from string to union type
  return [...new Set(loginPermissionsForServices)];
}

/**
 * Sometimes we want to specifically have a permission on one of several contexts. For example: An admin has a global context and some employee has a local context to edit entity x.
 * So we want to return true if the person has either the global or the local context. Global is more dominant in this regard and an admin might not have the local context.
 * @param user the user to check the permission on
 * @param contexts all the contexts to check trough
 */
export function hasPermissionOnAtleastOneContext(
  user: Maybe<ChancenwerkUser>,
  permission: AllPermissions,
  contexts: string[],
): boolean {
  if (!user) return false;
  return contexts.some(c => user.hasPermission(permission, c));
}
export function hasAnyPermission(user: Maybe<ChancenwerkUser>, permissions: AllPermissions[]) {
  return permissions.some(perm => hasPermissionOnAnyContext(user, perm));
}

export function hasPermissionOnAnyContext(user: Maybe<ChancenwerkUser>, permission: AllPermissions) {
  if (context?.serverContext) return true;
  if (!user) return false;
  return user.hasPermission(permission, ANY_CONTEXT);
}
